import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CreditCard, Banknote, Smartphone, Plus, CheckCircle2, User, Wallet, Trophy, RefreshCw } from 'lucide-react';
import api from '../services/api';

export default function Checkout({ cart, total, taxAmount, currencySymbol = '$', onFinish, close }) {
  const [paymentMethods, setPaymentMethods] = useState([{ method: 'cash', amount: 0 }]);
  const [customerId, setCustomerId] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [giftCardCode, setGiftCardCode] = useState('');
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);
  const [settings, setSettings] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [autoPrint, setAutoPrint] = useState(localStorage.getItem('pos-autoprint') === 'true');
  const navigate = useNavigate();

  const [gatewayConfig, setGatewayConfig] = useState(null);
  const [reference, setReference] = useState('');

  // Total is now passed as a prop, taking tax into account

  useEffect(() => {
    api.get('/payments/config').then(res => setGatewayConfig(res.data)).catch(console.error);
    api.get('/settings').then(res => setSettings(res.data)).catch(console.error);
    
    // Auto-fill payment amount with total
    const due = Math.max(0, total - (pointsToRedeem * (parseFloat(settings.points_conversion_rate) || 0.01)) - (appliedGiftCard ? appliedGiftCard.balance : 0));
    setPaymentMethods([{ method: 'cash', amount: Math.round(due * 100) / 100 }]);
  }, [total, settings.points_conversion_rate, pointsToRedeem, appliedGiftCard]);

  useEffect(() => {
    if (customerId && customerId.length >= 1) {
      const timer = setTimeout(() => {
        api.get(`/customers/${customerId}`)
          .then(res => setSelectedCustomer(res.data))
          .catch(() => setSelectedCustomer(null));
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setSelectedCustomer(null);
    }
  }, [customerId]);

  const addPayment = () => setPaymentMethods([...paymentMethods, { method: 'cash', amount: 0 }]);

  const submit = async () => {
    console.log('Submit initiated. Payment methods:', paymentMethods);
    setError('');
    const primaryMethod = paymentMethods[0].method;
    
    // Check Store Credit
    const creditPayment = paymentMethods.find(p => p.method === 'credit');
    if (creditPayment && (!selectedCustomer || selectedCustomer.balance < creditPayment.amount)) {
      console.log('Credit payment validation failed');
      setError('Insufficient store credit or no customer selected');
      return;
    }

    setIsProcessing(true);
    try {
      let finalRef = reference;
      console.log('Processing primary method:', primaryMethod);

      // 3. Bank Terminal Verification
      if (primaryMethod === 'bank' && !reference) {
        console.log('Bank reference missing');
        setError('Please enter the Bank Terminal Reference ID');
        setIsProcessing(false);
        return;
      }

      console.log('Proceeding to finishCheckout with ref:', finalRef);
      await finishCheckout(finalRef);
    } catch (e) {
      console.error('Submit error catch:', e);
      const errorMsg = e.response?.data?.error || e.response?.data?.message || e.message || 'Checkout failed';
      setError(errorMsg);
      setIsProcessing(false);
    }
  };

  const finishCheckout = async (ref) => {
    console.log('finishCheckout started. Ref:', ref);
    try {
      let finalPayments = paymentMethods.map(p => ({ ...p, reference: ref }));
      if (appliedGiftCard) {
        console.log('Applying gift card:', appliedGiftCard.code);
        finalPayments.push({ method: 'giftcard', amount: appliedGiftCard.balance, reference: appliedGiftCard.code });
      }
      
      console.log('Calling onFinish with payments:', finalPayments);
      const saleId = await onFinish(finalPayments, selectedCustomer?.id || null, discountCode, pointsToRedeem);
      console.log('onFinish result (saleId):', saleId);

      if (saleId) {
        console.log('Checkout successful, navigating to receipt');
        if (autoPrint) {
          navigate(`/app/receipt/${saleId}?autoprint=true`);
        } else {
          navigate(`/app/receipt/${saleId}`);
        }
      } else {
        console.log('onFinish returned no saleId (likely queued offline)');
      }
      close();
    } catch (e) {
      console.error('finishCheckout error catch:', e);
      const errorMsg = e.response?.data?.error || e.response?.data?.message || e.message || 'Checkout failed';
      setError(errorMsg);
      setIsProcessing(false);
    }
  };

  const getMethodIcon = (method) => {
    switch(method) {
      case 'card': return <CreditCard size={18} />;
      case 'mobile': return <Smartphone size={18} />;
      case 'credit': return <Wallet size={18} />;
      default: return <Banknote size={18} />;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md flex items-center justify-center z-50 animate-fade-in">
      <div className="bg-theme-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh] border border-theme">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-theme flex justify-between items-center bg-theme-main/10">
          <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Checkout</h3>
          <button onClick={close} className="text-theme-muted hover:text-theme-main p-1.5 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6 custom-scrollbar">
          {error && (
            <div className="bg-rose-500/10 text-rose-500 p-3 rounded-xl text-xs font-black border border-rose-500/20 text-center uppercase tracking-widest">
              {error}
            </div>
          )}

          <div className="bg-brand/10 rounded-2xl p-5 flex flex-col items-center justify-center border border-brand/20">
            <span className="text-brand text-[10px] font-black uppercase tracking-[0.2em] mb-2">Total Due</span>
            <span className="text-4xl font-black text-brand">
              {currencySymbol}{Math.max(0, total - (pointsToRedeem * (parseFloat(settings.points_conversion_rate) || 0.01)) - (appliedGiftCard ? appliedGiftCard.balance : 0)).toFixed(2)}
            </span>
            {appliedGiftCard && (
              <span className="text-emerald-500 text-[10px] font-black uppercase mt-1">
                Gift Card Applied: -{currencySymbol}{appliedGiftCard.balance.toFixed(2)}
              </span>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Gift Card Code</label>
              <div className="flex gap-2">
                <input 
                  className="flex-1 pl-4 pr-4 bg-theme-main border border-theme rounded-xl py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand transition-all text-theme-main" 
                  placeholder="Enter code..."
                  value={giftCardCode} 
                  onChange={e => setGiftCardCode(e.target.value)} 
                />
                <button 
                  onClick={async () => {
                    try {
                      const res = await api.get(`/giftcards/${giftCardCode}`);
                      setAppliedGiftCard(res.data);
                      setError('');
                    } catch (e) {
                      setError(e.response?.data?.error || 'Invalid gift card');
                      setAppliedGiftCard(null);
                    }
                  }}
                  className="bg-theme-card border border-theme text-theme-main px-4 rounded-xl text-xs font-black uppercase tracking-widest hover:border-brand transition-all"
                >
                  Apply
                </button>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Customer ID</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted" size={18} />
                <input 
                  className="w-full pl-10 pr-4 bg-theme-main border border-theme rounded-xl py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand transition-all text-theme-main" 
                  placeholder="Enter customer ID..."
                  value={customerId} 
                  onChange={e => setCustomerId(e.target.value)} 
                />
              </div>
              {selectedCustomer && (
                <div className="mt-2 p-3 bg-theme-main border border-theme rounded-xl flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-black text-theme-main">{selectedCustomer.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] uppercase font-black px-1.5 py-0.5 rounded border ${
                          selectedCustomer.tier === 'Gold' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' :
                          selectedCustomer.tier === 'Silver' ? 'bg-slate-500/10 text-slate-400 border-slate-500/20' :
                          'bg-orange-500/10 text-orange-500 border-orange-500/20'
                        }`}>
                          <Trophy size={10} className="inline mr-1" /> {selectedCustomer.tier}
                        </span>
                        <span className="text-[10px] text-emerald-500 font-bold uppercase tracking-tight">
                          {selectedCustomer.tier === 'Gold' ? '2.0x' : selectedCustomer.tier === 'Silver' ? '1.5x' : '1.0x'} Pts
                        </span>
                        <span className="text-xs text-theme-muted font-medium">{selectedCustomer.points} pts available</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-theme-muted uppercase font-black">Credit Balance</div>
                      <div className={`text-sm font-black ${selectedCustomer.balance >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                        ${selectedCustomer.balance.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {selectedCustomer.points > 0 && (
                    <div className="border-t border-theme pt-3 mt-1">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-[10px] font-black uppercase text-theme-muted">Redeem Points</span>
                        <span className="text-[10px] font-bold text-brand italic">
                          {settings.points_conversion_rate ? `${Math.round(1/settings.points_conversion_rate)} pts = ${currencySymbol}1` : '100 pts = $1'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <input 
                          type="number"
                          max={selectedCustomer.points}
                          value={pointsToRedeem}
                          onChange={(e) => setPointsToRedeem(Math.min(selectedCustomer.points, Math.max(0, parseInt(e.target.value) || 0)))}
                          className="flex-1 bg-theme-main border border-theme rounded-lg py-1.5 px-3 text-xs text-theme-main font-bold outline-none focus:ring-1 focus:ring-brand placeholder:text-theme-muted"
                          placeholder="Points..."
                        />
                        <button 
                          type="button"
                          onClick={() => setPointsToRedeem(selectedCustomer.points)}
                          className="px-3 py-1.5 bg-brand/10 text-brand text-[10px] font-black uppercase rounded-lg hover:bg-brand/20"
                        >
                          Max
                        </button>
                      </div>
                      {pointsToRedeem > 0 && (
                        <div className="mt-2 text-[10px] font-bold text-emerald-500 flex justify-between">
                          <span>Discount Applied:</span>
                          <span>-{currencySymbol}{(pointsToRedeem * (parseFloat(settings.points_conversion_rate) || 0.01)).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Discount Code</label>
              <input 
                className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-sm uppercase outline-none focus:ring-2 focus:ring-brand transition-all text-theme-main" 
                placeholder="e.g. SUMMER20"
                value={discountCode} 
                onChange={e => setDiscountCode(e.target.value.toUpperCase())} 
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-end mb-3">
              <h4 className="font-black text-theme-main text-sm uppercase tracking-tight">Payments</h4>
              <button onClick={addPayment} className="text-brand text-[10px] font-black flex items-center gap-1 bg-brand/10 hover:bg-brand/20 px-3 py-1.5 rounded-lg transition-colors uppercase tracking-widest">
                <Plus size={14} /> Split
              </button>
            </div>
            
            <div className="space-y-3">
              {paymentMethods.map((pm, idx) => (
                <div key={idx} className="flex gap-2 items-center bg-theme-main p-2.5 rounded-xl border border-theme">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-theme-muted">
                      {getMethodIcon(pm.method)}
                    </div>
                    <select 
                      className="w-full pl-10 bg-transparent py-2 text-sm font-bold text-theme-main focus:ring-0 cursor-pointer border-none outline-none"
                      value={pm.method} 
                      onChange={e => { 
                        const n = [...paymentMethods]; 
                        n[idx].method = e.target.value; 
                        setPaymentMethods(n); 
                      }}
                    >
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="bank">Bank Transfer / Terminal</option>
                      <option value="mobile">Mobile Wallet</option>
                      <option value="credit">Store Credit</option>
                    </select>
                  </div>
                  <div className="relative w-32">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-theme-muted font-bold">
                      $
                    </div>
                    <input 
                      type="number" 
                      min="0"
                      step="0.01"
                      className="w-full pl-7 pr-3 py-2 text-right bg-theme-card border border-theme rounded-lg shadow-sm font-black text-theme-main outline-none focus:ring-1 focus:ring-brand"
                      value={pm.amount} 
                      onChange={e => { 
                        const n = [...paymentMethods]; 
                        n[idx].amount = parseFloat(e.target.value) || 0; 
                        setPaymentMethods(n); 
                      }} 
                    />
                  </div>
                </div>
              ))}
              
              {paymentMethods.some(p => p.method === 'bank') && (
                <div className="animate-fade-in-up">
                  <label className="block text-[10px] font-black text-theme-muted mb-1 uppercase tracking-widest">Transaction Reference / Slip ID</label>
                  <input 
                    className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-sm font-mono outline-none focus:ring-2 focus:ring-brand transition-all text-theme-main" 
                    placeholder="Enter Reference Number..."
                    value={reference} 
                    onChange={e => setReference(e.target.value)} 
                  />
                  <p className="text-[10px] text-theme-muted mt-1 italic">Required for bank terminal reconciliation.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-theme bg-theme-main/5">
          <div className="flex items-center gap-3 mb-4 px-1">
            <div className="relative flex items-center">
              <input 
                type="checkbox" 
                id="autoprint" 
                checked={autoPrint} 
                onChange={(e) => {
                  setAutoPrint(e.target.checked);
                  localStorage.setItem('pos-autoprint', e.target.checked);
                }}
                className="w-5 h-5 rounded-md border-theme text-brand focus:ring-brand accent-brand cursor-pointer"
              />
            </div>
            <label htmlFor="autoprint" className="text-xs font-black text-theme-muted uppercase tracking-widest cursor-pointer select-none">
              Auto-print receipt
            </label>
          </div>
          
          <div className="flex gap-4">
            <button 
              onClick={close} 
              className="flex-1 px-6 py-4 bg-theme-card border border-theme text-theme-muted rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] hover:bg-theme-main transition-all hover:border-theme-main shadow-soft"
            >
              Cancel
            </button>
            <button 
              onClick={submit} 
              disabled={isProcessing}
              className="flex-[2] px-6 py-4 bg-brand text-white rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] transition-all shadow-xl shadow-brand/20 flex items-center justify-center gap-3 disabled:opacity-70 hover:bg-brand-light transform hover:-translate-y-0.5 active:translate-y-0"
            >
              {isProcessing ? (
                <RefreshCw size={20} className="animate-spin" />
              ) : (
                <>
                  <CheckCircle2 size={20} /> Complete Sale
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}